import React, { type FC, useState } from 'react'
import { observer } from 'mobx-react-lite'
import {
  Dropdown,
  type IFieldDropdownErrorProps,
  Scrollbar,
  type IDropdownProps,
  type IDropdownGroupProps,
} from 'shared/ui'
import {
  type ITextFieldBaseAsideAction,
  type ITextFieldPlaceholderProps,
  makeTextFieldError,
  TextFieldPlaceholder,
} from 'shared/ui/TextField'
import { SelectedItem } from 'widgets/SendFromMulti/ui/SelectedItem/SelectedItem'
import { useSendFromMultiContext } from 'widgets/SendFromMulti/context/context'
import { Trigger, type ITriggerProps } from './Trigger/Trigger'
import styles from './styles.module.scss'

export type ISendFromMultiUiProps = {
  width?: number
  rightActions?: ITextFieldBaseAsideAction[]
  onClearError?: () => void
  error?: IFieldDropdownErrorProps
  textFieldPlaceholderProps?: Partial<ITextFieldPlaceholderProps>
  triggerProps?: Partial<ITriggerProps>
  dropdownProps?: Partial<IDropdownProps>
  dropdownGroupProps?: Partial<IDropdownGroupProps>
  scrollBarMaxHeight?: number
  popperProps?: {
    offset?: [number, number]
  }
}

export const SendFromMultiUi: FC<ISendFromMultiUiProps> = observer(
  ({
    width = 338,
    rightActions,
    onClearError,
    error,
    textFieldPlaceholderProps,
    dropdownProps,
    dropdownGroupProps,
    triggerProps,
    scrollBarMaxHeight = 132,
    popperProps,
  }) => {
    const {
      error: storeError,
      setError,
      dropdownItems,
      setSelectedDropdownItem,
      selectedDropdownItems,
      removeSelectedDropdownItem,
      handleMultiSelect,
      handleMultiDeselect,
      dropdownItemsByGroup,
    } = useSendFromMultiContext()

    const [searchValue, setSearchValue] = useState('')
    const [hasSearchValue, setHasSearchValue] = useState(false)
    const { propsDropdownList, ...restDropdownProps } = dropdownProps || {}

    return (
      <Dropdown
        className={styles.dropdown}
        onToggle={(isOpen) => {
          setError('')
          onClearError?.()
          if (isOpen) {
            setSearchValue('')
            setHasSearchValue(false)
          }
        }}
        ariaLabel={'NumbersInboxesDropDown'}
        placement={'bottom-start'}
        items={dropdownItems.map((item) => ({
          ...item,
          childrenDropdownProps: {
            ...item.childrenDropdownProps,
            propsDropdownList: {
              withGrouping: true,
              dropdownGroupProps: {
                isMultiSelect: true,
                handleMultiSelect,
                stylesWrap: {
                  padding: 4,
                },
              },
            },
          },
        }))}
        onChange={(item) => {
          setSelectedDropdownItem(item)
          setSearchValue('')
        }}
        withSearch={Boolean(dropdownItems.length)}
        placeholder={dropdownProps?.placeholder ? dropdownProps.placeholder : 'Search'}
        width={width}
        margin={0}
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                offset: ({ reference }) => {
                  return popperProps?.offset ?? [-4, -reference.height - 4]
                },
              },
            },
          ],
        }}
        triggerComponent={() => (
          <TextFieldPlaceholder
            multiline
            className={styles.input}
            placeholder={<Trigger {...triggerProps} />}
            error={error?.message || storeError}
            rightActions={[
              makeTextFieldError({
                placement: error?.placement ?? 'right',
                boundaryElement: error?.boundaryElement,
                offset: error?.offset,
                anchor: error?.anchor,
              }),
              ...(rightActions || []),
            ]}
            {...textFieldPlaceholderProps}
          />
        )}
        propsDropdownList={{
          topListComponent: !!selectedDropdownItems.length && (
            <Scrollbar autoHeight autoHeightMin={0} autoHeightMax={scrollBarMaxHeight}>
              <div className={styles.list}>
                {selectedDropdownItems.map((item) => (
                  <SelectedItem
                    key={item.id}
                    item={item}
                    onDelete={removeSelectedDropdownItem}
                    showNumbersCount={triggerProps?.showNumbersCount}
                    hideNumbers={triggerProps?.hideNumbers}
                  />
                ))}
              </div>
            </Scrollbar>
          ),
          className: 'hasDropdownCardChild',
          maxHeight: 300,
          withGrouping: true,
          allHideOnScroll: true,
          dropdownGroupProps: {
            isScroll: false,
            isMultiSelect: dropdownGroupProps?.showSelectAllButton ?? false,
            handleMultiSelect,
            handleMultiDeselect,
            dropdownItemsByGroup,
            stylesWrap: dropdownGroupProps?.stylesWrap ?? undefined,
            showGroupHeader: !hasSearchValue,
          },
          withDeselectAll: dropdownGroupProps?.showSelectAllButton ?? false,
          search: searchValue,
          onSearchChange: (value) => {
            setSearchValue(value)
            setHasSearchValue(!!value)
          },
          ...propsDropdownList,
        }}
        {...restDropdownProps}
      />
    )
  }
)
